import React from 'react';

import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';

import { StatusSelector, ImpactSelector, ImpactCategorySelector, GeneralRisksSelector, GeneralDecisionsSelector, VenueSelector } from '../Selectors';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import ModalFooter from 'CommonComponents/ModalFooter';
import SelectFromField from 'CommonComponents/SelectFromField';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextFormField from 'CommonComponents/TextFormField';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { useUsers } from 'Hooks/useUsers';

import MinusIcon from 'Assets/icons/minus.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const PRIORITIES = ['low', 'medium', 'high'];
const DEFAULT_ACTION = { id: null, description: '', owner_id: '', date: '', completed_date: '' };
const GeneralIssueSchema = Yup.object().shape({
    description: Yup.string().required($t('This value is required')),
    detailed_description: Yup.string(),
    impact_description: Yup.string(),
    owner_id: Yup.string(),
    decision_maker: Yup.string().nullable(),
    outcome: Yup.string().nullable(),
    resolution_date: Yup.date().nullable(),
    priority: Yup.string().nullable().oneOf(PRIORITIES),
    general_status_id: Yup.string(),
    general_impact_id: Yup.string(),
    impact_category_ids: Yup.array(Yup.string()),
    general_risk_ids: Yup.array(Yup.string()),
    general_decision_ids: Yup.array(Yup.string()),
    venue_ids: Yup.array().of(Yup.string()),
    actions: Yup.array().of(
        Yup.object().shape({
            description: Yup.string().required($t('This value is required')),
            owner_id: Yup.string(),
            date: Yup.date().nullable(),
            completed_date: Yup.date().nullable(),
        }),
    ),
});

export default function GeneralIssueForm(props) {
    const { users } = useUsers();

    return (
        <Formik
            enableReinitialize
            initialValues={{
                description: props.issue?.description || '',
                detailed_description: props.issue?.detailed_description || '',
                impact_description: props.issue?.impact_description || '',
                owner_id: props.issue?.owner_id || '',
                decision_maker: props.issue?.decision_maker || '',
                outcome: props.issue?.outcome || '',
                resolution_date: props.issue?.resolution_date || '',
                priority: props.issue?.priority || '',
                general_status_id: props.issue?.general_status_id || '',
                general_impact_id: props.issue?.general_impact_id || '',
                impact_category_ids: props.issue?.impact_category_ids?.map(String) || [],
                general_risk_ids: props.issue?.general_risk_ids?.map(String) || [],
                general_decision_ids: props.issue?.general_decision_ids?.map(String) || [],
                venue_ids: props.issue?.venue_ids?.map(String) || [],
                actions:
                    props.issue?.actions?.map((action) => ({
                        ...action,
                        description: action.description ?? DEFAULT_ACTION.description,
                        owner_id: action.owner_id ?? DEFAULT_ACTION.owner_id,
                        date: action.date ?? DEFAULT_ACTION.date,
                        completed_date: action.completed_date ?? DEFAULT_ACTION.completed_date,
                    })) || [],
            }}
            validationSchema={GeneralIssueSchema}
            onSubmit={(values, actions) => {
                if (props.issue?.id) {
                    if (props.currentVenue) {
                        // if the issue is being edited from a venue context, we want to keep the issue associated to the current venue only, as we don't want the user to edit the venues from this form
                        values.venue_ids = props.issue?.venue_ids?.map(String) || [];
                    }

                    const updateUrl = props.updateUrl.replace('GENERAL_ISSUE_ID', props.issue.id);

                    axios
                        .put(updateUrl, values)
                        .then((res) => {
                            actions.resetForm();
                            props.onSuccess();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                } else {
                    if (props.currentVenue) {
                        // if the issue is being created from a venue context, we want to automatically associate the issue to the current venue, as we don't want the user to select the venues from this form
                        values.venue_ids = [props.currentVenue.id];
                    }

                    axios
                        .post(props.storeUrl, values)
                        .then((res) => {
                            actions.resetForm();
                            props.onSuccess();
                        })
                        .catch((e) => {
                            e?.response?.data?.errors && actions.setErrors(e?.response?.data?.errors);
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                }
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                <>
                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <TextFormField
                            label={$t('Description')}
                            type="text"
                            required={true}
                            name="description"
                            placeholder={$t('Enter issue description')}
                        ></TextFormField>

                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label className="text-input__label">{$t('Owner')}</label>
                            <UserSelectorInput
                                selectedId={values.owner_id}
                                onChange={(value) => setFieldValue('owner_id', value)}
                                usersList={users}
                                fetchIfNoUsers={false}
                            />
                            {errors.owner_id && touched.owner_id ? <div className="tw-text-red-500 tw-text-sm">{errors.owner_id}</div> : null}
                        </div>
                    </div>

                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <SelectFromField label={$t('Priority')} as="select" name="priority">
                            <option>Select a priority</option>
                            {PRIORITIES.map((priority) => (
                                <option key={priority} value={priority}>
                                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                </option>
                            ))}
                        </SelectFromField>

                        <StatusSelector
                            name="general_status_id"
                            statuses={props.datasets.statuses}
                            errors={errors.general_status_id}
                            touched={touched.general_status_id}
                        />
                    </div>

                    {props.allowVenueSelection && (
                        <div className="tw-grid tw-grid-cols-1">
                            <VenueSelector
                                venuesOptions={props.datasets.venuesOptions}
                                values={values}
                                onChange={(venues) => setFieldValue('venue_ids', venues?.map((venue) => venue.value) || [])}
                                disabled={isSubmitting}
                                errors={errors.venue_ids}
                                touched={touched.venue_ids}
                            />
                        </div>
                    )}

                    <div className="tw-grid tw-grid-cols-1">
                        <TextareaFormField
                            label={$t('Detailed description')}
                            as="textarea"
                            type="text"
                            name="detailed_description"
                            placeholder={$t("Enter issue's detailed description")}
                        ></TextareaFormField>
                    </div>

                    <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <ImpactSelector
                            name="general_impact_id"
                            impacts={props.datasets.impacts}
                            errors={errors.general_impact_id}
                            touched={touched.general_impact_id}
                        />
                        <ImpactCategorySelector
                            name="impact_category_ids"
                            isSpanToTwo={true}
                            categories={props.datasets.impactCategories}
                            errors={errors.impact_category_ids}
                            touched={touched.impact_category_ids}
                        />
                    </div>

                    <div className="tw-grid tw-grid-cols-1">
                        <TextareaFormField
                            label={$t('Impact description')}
                            as="textarea"
                            type="text"
                            name="impact_description"
                            placeholder={$t('Enter impact description')}
                        ></TextareaFormField>
                    </div>

                    <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                    <FieldArray
                        name="actions"
                        render={(arrayHelpers) => (
                            <>
                                {!!values.actions?.length &&
                                    values.actions.map((action, index) => {
                                        const actionErrors = errors.actions && errors.actions[index];
                                        const actionTouched = touched.actions && touched.actions[index];
                                        return (
                                            <div
                                                className="tw-flex tw-flex-col sm:tw-flex-row tw-space-y-4 sm:tw-space-y-0 tw-space-x-0 sm:tw-space-x-4"
                                                key={index}
                                            >
                                                <div className="tw-w-full">
                                                    <TextareaFormField
                                                        label={$t('Action')}
                                                        required={true}
                                                        as="textarea"
                                                        name={`actions.${index}.description`}
                                                        placeholder={$t('Enter action description')}
                                                    ></TextareaFormField>
                                                </div>
                                                <div className="tw-grid tw-grid-rows-2 tw-gap-4 tw-w-ull">
                                                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                            <label className="text-input__label">{$t('Owner')}</label>
                                                            <UserSelectorInput
                                                                selectedId={action.owner_id}
                                                                onChange={(value) => setFieldValue(`actions.${index}.owner_id`, value)}
                                                                usersList={users}
                                                                fetchIfNoUsers={false}
                                                            />
                                                            {actionErrors?.owner_id && actionTouched?.owner_id ? (
                                                                <div className="tw-text-red-500 tw-text-sm">{actionErrors?.owner_id}</div>
                                                            ) : null}
                                                        </div>
                                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                            <label className="text-input__label">{$t('Due Date')}</label>
                                                            <DatePickerField
                                                                name={`actions.${index}.date`}
                                                                valueFormat="yyyy-MM-dd"
                                                                dateFormat="yyyy-MM-dd"
                                                                className={
                                                                    actionErrors?.date && actionTouched?.date
                                                                        ? 'tw-border tw-border-red-500 text-input'
                                                                        : 'tw-border-0 text-input'
                                                                }
                                                                value={values?.actions[index]?.date}
                                                                onChange={setFieldValue}
                                                            />
                                                            {actionErrors?.date && actionTouched?.date && (
                                                                <div className="tw-text-red-500 tw-text-sm">{actionErrors?.date}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="tw-grid tw-grid-cols-1">
                                                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                            <label className="text-input__label">Completed</label>
                                                            <DatePickerField
                                                                name={`actions.${index}.completed_date`}
                                                                className={
                                                                    actionErrors?.completed_date && actionTouched?.completed_date
                                                                        ? 'tw-border tw-border-red-500 text-input'
                                                                        : 'tw-border-0 text-input'
                                                                }
                                                                value={values?.actions[index]?.completed_date}
                                                                onChange={setFieldValue}
                                                            />
                                                            {actionErrors?.completed_date && actionTouched?.completed_date && (
                                                                <div className="tw-text-red-500 tw-text-sm">{actionErrors?.completed_date}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="">
                                                    <Button small variant="danger" className="mt-4" onClick={() => arrayHelpers.remove(index)}>
                                                        <MinusIcon className="tw-w-3.5 tw-h-3.5" />
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                <div className="tw-flex">
                                    <Button small variant="primary" onClick={() => arrayHelpers.push(DEFAULT_ACTION)}>
                                        <PlusIcon className="tw-w-3 tw-h-3" aria-hidden="true" />
                                        {$t('Add new action')}
                                    </Button>
                                </div>
                            </>
                        )}
                    />

                    <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                    <div className="tw-grid tw-grid-cols-1">
                        <TextareaFormField label={$t('Issue Outcome')} as="textarea" type="text" name="outcome"></TextareaFormField>
                    </div>

                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                        <div className="tw-flex tw-flex-col tw-space-y-1.5">
                            <label htmlFor="resolution_date" className="tw-text-charcoal tw-text-sm tw-mb-0 tw-capitalize">
                                {$t('Resolution date')}
                            </label>
                            <DatePickerField
                                name="resolution_date"
                                className={
                                    errors.resolution_date && touched.resolution_date ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'
                                }
                                value={values.resolution_date}
                                onChange={setFieldValue}
                            />
                            {errors.resolution_date && touched.resolution_date && <div className="tw-text-red-500 tw-text-sm">{errors.resolution_date}</div>}
                        </div>
                        <TextFormField label={$t('Decision Maker')} type="text" name="decision_maker"></TextFormField>
                    </div>

                    <div className="tw-w-full tw-border-b tw-border-2 tw-border-grey"></div>

                    <div className="tw-grid tw-grid-cols-1 tw-gap-4">
                        <h6 className="tw-text-xl tw-text-charcoal tw-font-semibold">{$t('Inter-dependencies')}</h6>
                        <GeneralRisksSelector
                            name="general_risk_ids"
                            risks={props.datasets.risks}
                            errors={errors.general_risk_ids}
                            touched={touched.general_risk_ids}
                        />
                        <GeneralDecisionsSelector
                            name="general_decision_ids"
                            decisions={props.datasets.decisions}
                            errors={errors.general_decision_ids}
                            touched={touched.general_decision_ids}
                        />
                    </div>

                    <ModalFooter
                        cancelButtonText={$t('Cancel')}
                        primaryButtonText={$t('Save')}
                        onCancel={props.onCancel}
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                    ></ModalFooter>
                </>
            )}
        </Formik>
    );
}
